import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IAirportSelectOutput } from "libraries/component/airport-select/airport-select.component";
import { FormArray, FormBuilder, FormGroup, FormGroupDirective, Validators } from "@angular/forms";
import { Yii } from "libraries/interfaces/Yii";
import { Subscription } from "rxjs";
import { IValidator } from "libraries/module/formbuilder/forms/utils/validators/IValidator";
import * as moment from "moment";
import { wenji } from "libraries/interfaces/IWenji";
import { DateComponent } from "libraries/module/formbuilder/fields/date/date.component";
import { EAirSearchFields } from "app_locobook/air-search/e-air-search-fields";
// import { EWayType, IAirSearchSegment, IBegin } from '../air-search-segment-multiple/air-search-segment-multiple.component';

export interface IDuration {
    max?: number;
    unit?: string;
    xUnit?: string;
}

export interface IBetween {
    min?: {
        time?: string;
    };
    max: {
        time?: string;
    };
}

export interface IBegin {
    value: Date;
    between?: IBetween;
}

export interface IEnd {
    xMoment: string;
    between?: IBetween;
}

export interface IAirSearchSegment {
    begin: Date | IBegin;
    end?: IEnd;
    from: string | IAirport;
    isFromAirport: boolean;
    to: string | IAirport;
    isToAirport: boolean;
    maxVias?: number;
    cabin?: string;
    duration?: IDuration;
    viaArray?: {
        duration?: IDuration;
    }[]
}

export interface IAirport {
    airport: {
        iata: string;
    };
}

export enum EWayType {
    Oneway = "Oneway",
    Return = "Return",
    Multiple = "Multiple"
}

class Wording {
    public readonly oneWay: string = Yii.t('wui-shared-js', 'Aller Simple');
    public readonly return: string = Yii.t('wui-shared-js', 'Aller - Retour');
    public readonly multiple: string = Yii.t('wui-shared-js', 'Itinéraire');
    public readonly onlyAirport: string = Yii.t('wui-iqba-js', 'Cet aéroport uniquement');
    public readonly dateBegin: string = Yii.t('wui-shared-js', 'Date Aller');
    public readonly dateEnd: string = Yii.t('wui-shared-js', 'Date Retour');
    public readonly date: string = Yii.t('wui-shared-js', 'Date');
    public readonly invertFromTo: string = Yii.t('wui-shared-js', 'Inverser les lieux de départ et de destination');
}

@Component({
    selector: 'air-search-segment-oneway-return',
    templateUrl: './air-search-segment-oneway-return.component.html',
    styleUrls: ['./air-search-segment-oneway-return.component.scss']
})
export class AirSearchSegmentOnewayReturnComponent implements OnInit, OnChanges, OnDestroy {
    @Output() emitSegmentValues: EventEmitter<IAirSearchSegment[]> = new EventEmitter<IAirSearchSegment[]>();
    @Input() wayType: EWayType;
    @Input() segmentValues: IAirSearchSegment[];
    @ViewChild('returnDate')
    public readonly returnDate: DateComponent;
    public readonly today: Date = new Date();
    public minReturnDate: Date = this.today;
    public onlyOneMonth: boolean;
    public airSearchForm: FormGroup;
    public wording: Wording = new Wording();
    public eWayType: typeof EWayType = EWayType;
    public eSegmentFields: typeof EAirSearchFields = EAirSearchFields;
    public airSegmentSearchForm: FormArray;
    public dateValidator: IValidator[] = [
        {
            validator: Validators.required,
            errors: {
                required: {
                    when: 'touched',
                    message: Yii.t('wui-shared-js', 'Date requise'),
                }
            }
        }
    ];
    public airSearchSegmentFormSubscription: Subscription;

    constructor(
        private formGroupDirective: FormGroupDirective,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef
    ) { }

    public ngOnInit(): void {
        this.initSegmentForm();
        this.initAirSearchSegmentFormSubscription();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.wayType?.currentValue && !changes?.wayType?.isFirstChange()) {
            this.wayTypeChange();
            this.cdr.detectChanges();
        }
    }

    public ngOnDestroy(): void {
        this.emitSegmentValues.emit(this.airSegmentSearchForm.value);
        this.airSearchForm.removeControl(EAirSearchFields.segment);
        this.airSearchSegmentFormSubscription.unsubscribe();
    }

    public patchSegment(location: IAirportSelectOutput, field: string): void {
        if (location) {
            this.airSegmentSearchForm.get([0, field]).patchValue(location.code);
            this.airSegmentSearchForm.get([0, field === EAirSearchFields.from ? EAirSearchFields.isFromAirport : EAirSearchFields.isToAirport]).patchValue(location.isAirportOnly);
            if (this.wayType === this.eWayType.Return) {
                this.airSegmentSearchForm.get([1, field === EAirSearchFields.from ? EAirSearchFields.to : EAirSearchFields.from]).patchValue(location.code);
                this.airSegmentSearchForm.get([1, field === EAirSearchFields.from ? EAirSearchFields.isToAirport : EAirSearchFields.isFromAirport]).patchValue(location.isAirportOnly);
            }
        } else {
            /*TODO : gérer le cas où si on saisi une valeur au From et on la efface par la suite, bien maj le formulaire envoie du vide et pas l'ancienne valeur*/
        }
    }

    public openAndFocusReturnDate(): void {
        if (this.wayType === EWayType.Return && this.returnDate) {
            this.returnDate.asyncToggle();
        }
    }

    private wayTypeChange(): void {
        if (this.wayType === EWayType.Oneway) {
            this.removeReturn();
        } else if (this.wayType === EWayType.Return) {
            this.addReturn();
        }
    }

    private initSegmentForm(): void {
        this.airSegmentSearchForm = this.fb.array([
            this.fb.group({
                from: [null, Validators.required],
                isFromAirport: [null],
                to: [null, Validators.required],
                isToAirport: [null],
                begin: [null, Validators.required]
            })
        ]);
        if (this.segmentValues) {
            this.segmentValues = this.segmentValues?.map((segment: IAirSearchSegment, index: number) => {
                return {
                    ...segment,
                    begin: (segment.begin as IBegin).value
                };
            });
            this.airSegmentSearchForm.patchValue(this.segmentValues ?? null, {emitEvent: false});
        }
        if (this.wayType === EWayType.Return) {
            this.addReturn();
        }
        this.airSearchForm = this.formGroupDirective.form;
        this.airSearchForm.addControl(EAirSearchFields.segment, this.airSegmentSearchForm);
    }

    private initAirSearchSegmentFormSubscription(): void {
        this.airSearchSegmentFormSubscription = this.airSegmentSearchForm.get([0, EAirSearchFields.begin]).valueChanges.subscribe((beginValue: Date) => {
            if (beginValue && this.wayType === EWayType.Return) {
                this.minReturnDate = beginValue;
                const hasReturnDate: boolean = Boolean(this.airSegmentSearchForm.get([1, EAirSearchFields.begin]).value);
                const beginDate: Date = this.airSegmentSearchForm.get([0, EAirSearchFields.begin]).value;
                const returnDate: Date = this.airSegmentSearchForm.get([1, EAirSearchFields.begin]).value;
                if (!hasReturnDate || beginDate < returnDate) {
                    const returnDateDays: Date = moment(beginValue).add(wenji.locobook.search.daysroundtrip, 'days').toDate();
                    this.airSegmentSearchForm.get([1, EAirSearchFields.begin]).patchValue(returnDateDays);
                }
                this.openAndFocusReturnDate();
            }
        });
    }

    private addReturn(): void {
        this.addSegmentGroup();
        const getFirstSegment: IAirSearchSegment = {
            from: this.airSegmentSearchForm.get([0, EAirSearchFields.to]).value,
            to: this.airSegmentSearchForm.get([0, EAirSearchFields.from]).value,
            isFromAirport: this.airSegmentSearchForm.get([0, EAirSearchFields.isToAirport]).value,
            isToAirport: this.airSegmentSearchForm.get([0, EAirSearchFields.isFromAirport]).value,
            begin: null,
        };
        this.airSegmentSearchForm.at(1).patchValue(getFirstSegment, {emitEvent: false});
    }

    private removeReturn(): void {
        this.airSegmentSearchForm.removeAt(1);
    }

    private addSegmentGroup(): void {
        this.airSegmentSearchForm.push(this.fb.group({
            from: [null, Validators.required],
            isFromAirport: [null],
            to: [null, Validators.required],
            isToAirport: [null],
            begin: [null, Validators.required]
        }));
    }
}
